import { observer } from 'mobx-react-lite';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import { Button } from '@linktivity/link-ui';
import { isHardware } from '@triplabo/web/utils';
import { Close } from '@linktivity/link-icons';
import { Confirm } from '@triplabo/web/components';
import EmptyBusy from '@triplabo/web/assets/empty_busy.svg?react';
import SearchAnimation from './search.json';

import styles from './waiting.module.css';

const POLLING_RECALL_INTERVAL = 5000;
const POLLING_CONTINUE_INTERVAL = 60000;

const CallWaiting: React.FC<{
  handleClose: () => void;
  recallOrder: () => void;
}> = observer(({ handleClose, recallOrder }) => {
  const { t } = useTranslation();
  const [isContinueCall, setIsContinueCall] = useState(false);
  const [showContinueDialog, setShowContinueDialog] = useState(false);
  const continueTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isContinueCall) {
      const recallTimer = setInterval(() => {
        recallOrder();
      }, POLLING_RECALL_INTERVAL);

      continueTimerRef.current = setInterval(() => {
        setShowContinueDialog(true);
      }, POLLING_CONTINUE_INTERVAL);

      return () => {
        clearInterval(recallTimer);
        if (continueTimerRef.current) {
          clearInterval(continueTimerRef.current);
        }
      };
    }
  }, [isContinueCall, recallOrder]);

  const handleRecallOrder = () => {
    setIsContinueCall(true);
  };

  const handleContinueWaiting = () => {
    setShowContinueDialog(false);
    setIsContinueCall(true);
    if (continueTimerRef.current) {
      clearInterval(continueTimerRef.current);
      continueTimerRef.current = setInterval(() => {
        setShowContinueDialog(true);
      }, POLLING_CONTINUE_INTERVAL);
    }
  };

  const DefaultRender = () => {
    return (
      <>
        <div className={styles.cardHeader}>
          <button className={styles.close} onClick={handleClose}>
            <Close />
          </button>
        </div>
        <div className={styles.waitingContent}>
          <EmptyBusy className={styles.emptyBg} />
          <p className={styles.title}>
            {t('views.orderConfirm.callWaiting.title')}
          </p>
          <p className={styles.subtitle}>
            {t('views.orderConfirm.callWaiting.subtitle')}
          </p>
          <div className={styles.buttonWrapper}>
            <Button
              variant="outlined"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              {t('views.orderConfirm.callWaiting.cancel')}
            </Button>
            <Button
              className={styles.confirmButton}
              onClick={handleRecallOrder}
            >
              {t('views.orderConfirm.callWaiting.confirm')}
            </Button>
          </div>
        </div>
      </>
    );
  };

  const ContinueCallRender = () => {
    return (
      <>
        <div className={styles.cardFindingHeader}>
          <div className={styles.findingText}>
            <span className={styles.finding}>
              {t('views.orderConfirm.callWaiting.finding')}
            </span>
            <span className={styles.dispatching}>
              {t('views.orderConfirm.callWaiting.dispatching')}
            </span>
          </div>
          <Button
            variant="outlined"
            className={styles.cancelSearch}
            onClick={handleClose}
          >
            {t('views.orderConfirm.callWaiting.cancel')}
          </Button>
        </div>
        <div className={styles.lottieContent}>
          <Lottie animationData={SearchAnimation} loop autoplay />
        </div>
      </>
    );
  };

  return (
    <div className={isHardware ? styles.hardware : styles.normal}>
      <div className={styles.cardWrapper}>
        <div className={styles.card}>
          {isContinueCall ? <ContinueCallRender /> : <DefaultRender />}
        </div>
        <Confirm
          className={styles.scale}
          show={showContinueDialog}
          content={t('views.orderConfirm.callWaiting.continueWaiting')}
          confirmText={t('views.orderConfirm.callWaiting.continue')}
          cancelText={t('views.orderConfirm.callWaiting.unwait')}
          onConfirm={handleContinueWaiting}
          onClose={handleClose}
        />
      </div>
    </div>
  );
});

export default CallWaiting;
